import React, { useState, useEffect, useMemo } from 'react';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker as MuiDatePicker } from '@mui/x-date-pickers/DatePicker';
import { StaticDatePicker } from '@mui/x-date-pickers/StaticDatePicker';
import { koKR } from '@mui/x-date-pickers/locales';
import { styled } from '@mui/material/styles';
import { BottomSheet } from 'react-spring-bottom-sheet';
import 'react-spring-bottom-sheet/dist/style.css';
import dayjs from 'dayjs';
import 'dayjs/locale/ko';
import calendarIcon from '../../assets/icon/16_calendar_black.svg';
import arrowIcon from '../../assets/icon/16_down_gray.svg';
import { useHolidays } from './useHolidays';

const DatePickerWrapper = styled('div')({
  flex: 1,
  width:'100%',
  
  '& .MuiTextField-root': {
    width: '100%',
    cursor: 'pointer',
    borderRadius:'2px',
    height: '40px',
  },
  '& .MuiInputBase-root': {
    cursor: 'pointer',
  },
  '& .MuiInputBase-input': {
    cursor: 'pointer',
    caretColor: 'transparent',
  },
  '& .MuiPickersDay-root.Mui-disabled': {
    color: 'var(--gray-50) !important',
  },
  '& .MuiPickersDay-root:not(.Mui-disabled)': {
    color: '#000 !important',
  },
  '& .css-q7mezt' :{
    backgroundImage: `url(${calendarIcon})`,
    backgroundSize: 'contain !important', 
    width: '16px',
    height: '16px', 
  },

//   '@media (max-width:768px)': {
//   '& .calendar-adornment': {
//     backgroundImage: `url(${calendarIcon})`,
//     backgroundSize: 'contain',
//     width: '16px',
//     height: '16px',
//   },
// },
  // '& .calendar-adornment': {
  //   backgroundImage: `url(${calendarIcon})`,
  //   backgroundSize: 'contain',
  //   backgroundRepeat:'no-repeat',
  //   width: '100%',
  //   height: '16px',
  //   position:'absolute',
  //   left:'12px',
  // },

  '& .css-q7mezt>path' :{
    display:'none', 
  },
  '& .css-1kmkvia' :{
    font:'var(--body2-1)',
    paddingLeft: '20px !important',
    height:'100%'
  },
  '& .css-yxqbup' :{
    marginLeft:'0'
  },
  '& .css-5v2ak0' :{
  border: '1px solid var(--gray-30) !important'
  },
  '&  .Mui-selected ' :{
    backgroundColor: 'var(--yellow-20) !important',
    color:'#000 !important',
  },
 
});

const BottomSheetWrapper = styled('div')({
  '& .react-spring-bottom-sheet': {
    '--rsbs-bg': '#fff',
    '--rsbs-handle-bg': '#e2e8f0',
    height:'60%'
  },
  '& .MuiPickersLayout-root': {
    display: 'flex',
    flexDirection: 'column',
  },
  // 바텀시트 내부 달력 스타일링
  '& .MuiPickersDay-root.Mui-disabled': {
    color: 'var(--gray-50) !important',
  },
  '& .MuiPickersDay-root:not(.Mui-disabled)': {
    color: '#000 !important',
  },

});

const DatePicker = ({ 
  type, 
  isDisabled = false,
  placeholder = '일자 선택',
  minDate,
  maxDate,
  onDateChange,
  value = dayjs(),
  shouldDisableDate,
  disableHolidays = false, // 공휴일 비활성화 옵션
  disableWeekends = false  // 주말 비활성화 옵션 추가
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [selectedDate, setSelectedDate] = useState(value);

  // 공휴일 데이터를 위한 연도 계산
  const targetYears = useMemo(() => {
    if (!disableHolidays) return [];
    
    const currentYear = dayjs().year();
    const nextYear = currentYear + 1;
    const prevYear = currentYear - 1;
    return [prevYear, currentYear, nextYear];
  }, [disableHolidays]);

  // 공휴일 Hook 사용
  const { isHoliday, loading: holidaysLoading } = useHolidays(targetYears);

  // value prop이 변경될 때 selectedDate 업데이트
  useEffect(() => {
    setSelectedDate(value);
  }, [value]);

  // 모바일 체크
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // 날짜 비활성화 로직 수정
  const shouldDisableDateWithHolidays = useMemo(() => {
    return (date) => {
      // 기존 shouldDisableDate 함수가 있으면 먼저 확인
      if (shouldDisableDate && shouldDisableDate(date)) {
        return true;
      }

      // 주말 체크
      if (disableWeekends) {
        const dayOfWeek = date.day();
        if (dayOfWeek === 0 || dayOfWeek === 6) { // 일요일(0) 또는 토요일(6)
          return true;
        }
      }

      // 공휴일 체크
      if (disableHolidays) {
        if (!holidaysLoading) {
          return isHoliday(date);
        }
        // 로딩 중일 때는 false 반환 (비활성화하지 않음)
        return false;
      }

      return false;
    };
  }, [shouldDisableDate, disableHolidays, disableWeekends, holidaysLoading, isHoliday]);

  const handleDateChange = (newDate) => {
    if (!newDate || !newDate.isValid()) return;
    
    // 주말 체크
    if (disableWeekends) {
      const dayOfWeek = newDate.day();
      if (dayOfWeek === 0 || dayOfWeek === 6) {
        return; // 주말이면 선택 불가
      }
    }
    
    // 공휴일 체크
    if (disableHolidays && !holidaysLoading) {
      if (isHoliday(newDate)) {
        return; // 공휴일이면 선택 불가
      }
    }
    
    setSelectedDate(newDate);
    if (onDateChange) {
      onDateChange(newDate);
    }
    if (isMobile) {
      setIsOpen(false);
    }
  };

  const handleInputClick = () => {
    if (!isDisabled && isMobile) {
      setIsOpen(true);
    }
  };

  return (
    <LocalizationProvider 
      dateAdapter={AdapterDayjs}
      adapterLocale="ko"
      localeText={koKR.components.MuiLocalizationProvider.defaultProps.localeText}
    >
      {/* <DatePickerWrapper> */}
      <DatePickerWrapper style={{ position: 'relative' }}>
        {/* 모바일에서 캘린더 아이콘 */}
        {isMobile && (
          <div style={{
            position: 'absolute',
            left: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            width: '16px',
            height: '16px',
            backgroundImage: `url(${calendarIcon})`,
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'contain',
            pointerEvents: 'none',
            zIndex: 1
          }} />
        )}
        {/* 모바일에서 화살표 아이콘 */}
        {isMobile && (
          <div style={{
            content: '',
            display: 'block',
            position: 'absolute',
            top:'7px',
            right: '8px',
            zIndex: 1,
            width: '16px',
            height: '16px',
            margin: '4px',
            backgroundImage: `url(${arrowIcon})`,
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'contain',
            pointerEvents: 'none',
          }} />
        )}
        <MuiDatePicker
          format="YYYY.MM.DD"
          value={selectedDate}
          onChange={isMobile ? undefined : handleDateChange}
          minDate={minDate ? dayjs(minDate) : undefined}
          maxDate={maxDate ? dayjs(maxDate) : undefined}
          disabled={isDisabled}
          disableOpenPicker={isMobile}
          shouldDisableDate={shouldDisableDateWithHolidays} // 공휴일 비활성화 함수 적용
          slotProps={{
            textField: {
              size: "small",
              fullWidth: true,
              placeholder: placeholder,
              inputProps: {
                readOnly: true
              },
              onClick: handleInputClick,
              InputProps: {
                classes: {
                  adornment: 'calendar-adornment'
                }
              }
            },
            inputAdornment: {
              className: 'calendar-adornment',
              onClick: handleInputClick
            },
            day: {
              sx: {
                '&.Mui-selected': {
                  backgroundColor: '#fdd835 !important',
                  color: '#000 !important',
                },
              },
            }
          }}

          componentsProps={{
            actionBar: {
              actions: ['cancel', 'accept']
            }
          }}
          PopperProps={{
            sx: isMobile ? { display: 'none' } : undefined
          }}
        />
      </DatePickerWrapper>
      
      {isMobile && (
        <BottomSheetWrapper>
          <BottomSheet
            open={isOpen}
            onDismiss={() => setIsOpen(false)}
            snapPoints={({ maxHeight }) => [maxHeight - 500]}
            defaultSnap={({ lastSnap, snapPoints }) => 
              Math.min(snapPoints[0], lastSnap ?? 0)}
          >
            <StaticDatePicker
              format="YYYY.MM.DD"
              displayStaticWrapperAs="desktop"
              value={selectedDate}
              onChange={handleDateChange}
              minDate={minDate ? dayjs(minDate) : undefined}
              maxDate={maxDate ? dayjs(maxDate) : undefined}
              disabled={isDisabled}
              shouldDisableDate={shouldDisableDateWithHolidays} // 공휴일 비활성화 함수 적용
              slotProps={{
                toolbar: { hidden: true },
                day: {
                  sx: {
                    '&.Mui-selected': {
                      backgroundColor: '#fdd835 !important',
                      color: '#000 !important',              
                    },
                  },
                },
              }}
            />
          </BottomSheet>
        </BottomSheetWrapper>
      )}
    </LocalizationProvider>
  );
};

export default DatePicker;